import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, X } from "lucide-react";
import {
  qrtListPlans, qrtCreatePlan, qrtUpdatePlan,
  qrtListCreditPlans, qrtUpdateCreditPlan,
  qrtListAddonPlans, qrtCreateAddonPlan, qrtUpdateAddonPlan,
} from "../../apis/qrtAdmin";
import { Spinner, fmtMoney } from "./qrtUi";
import { catalogVersion, planFamily, isTrialPlan } from "../../utils/planPricing";

const emptyPlanDraft = {
  plan_name: "",
  plan_display_name: "",
  monthly_amount: "",
  yearly_amount: "",
  yearly_discount: "500",
  duration_days: "30",
  max_flows: "1",
  max_numbers: "2",
  ai_credits: "0",
  ai_credit_plan_id: "",
  features: "",
  is_active: true,
  catalog_version: 1,
  plan_family: "",
  price_1_month: "",
  price_3_months: "",
  price_6_months: "",
  price_12_months: "",
};

const priceOf = (plan, period) => {
  const row = (plan.prices || []).find((p) => p.billing_period === period);
  return row ? String(row.amount) : "";
};

const planToDraft = (p) => ({
  plan_name: p.plan_name,
  plan_display_name: p.plan_display_name,
  monthly_amount: String(p.monthly_amount ?? ""),
  yearly_amount: String(p.yearly_amount ?? ""),
  yearly_discount: String(p.yearly_discount ?? "0"),
  duration_days: String(p.duration_days ?? "30"),
  max_flows: String(p.max_flows ?? "0"),
  max_numbers: String(p.max_numbers ?? "2"),
  ai_credits: String(p.ai_credits ?? "0"),
  ai_credit_plan_id: p.ai_credit_plan_id ? String(p.ai_credit_plan_id) : "",
  features: (() => {
    try {
      const arr = typeof p.features === "string" ? JSON.parse(p.features || "[]") : p.features || [];
      return arr.join("\n");
    } catch {
      return "";
    }
  })(),
  is_active: p.is_active === 1 || p.is_active === true,
  catalog_version: catalogVersion(p),
  plan_family: planFamily(p),
  price_1_month: priceOf(p, "1_month"),
  price_3_months: priceOf(p, "3_months"),
  price_6_months: priceOf(p, "6_months"),
  price_12_months: priceOf(p, "12_months"),
});

const PlanEditor = ({ title, draft, setDraft, creditPlans, onSave, onClose, busy, isNew }) => (
  <>
    <div className="ping-drawer-backdrop" onClick={onClose} />
    <aside className="ping-drawer" role="dialog" aria-modal="true">
      <div className="ping-drawer-head">
        <div>
          <h3>{title}</h3>
          <p>{Number(draft.catalog_version) === 2 ? "New catalog amounts are exclusive of GST" : "Prices in ₹ · yearly display price = monthly − discount"}</p>
        </div>
        <button type="button" className="camp-ico" onClick={onClose} aria-label="Close"><X size={16} /></button>
      </div>
      <div className="ping-drawer-body">
        {isNew && Number(draft.catalog_version) !== 2 && (
          <label>
            Plan key (unique, lowercase)
            <input value={draft.plan_name} onChange={(e) => setDraft({ ...draft, plan_name: e.target.value })} placeholder="e.g. growth" />
          </label>
        )}
        {Number(draft.catalog_version) === 2 && (
          <p style={{ fontSize: 12.5, color: "var(--text-3)", margin: "0 0 8px" }}>
            New catalog · key {draft.plan_name} (not editable)
          </p>
        )}
        <label>
          Display name
          <input value={draft.plan_display_name} onChange={(e) => setDraft({ ...draft, plan_display_name: e.target.value })} placeholder="Growth Plan" />
        </label>
        {Number(draft.catalog_version) === 2 ? (
          isTrialPlan(draft) ? (
            <label>
              1-month amount (₹)
              <input type="number" min="0" value={draft.price_1_month} onChange={(e) => setDraft({ ...draft, price_1_month: e.target.value, monthly_amount: e.target.value })} />
            </label>
          ) : (
            <>
              <label>
                3-month amount (₹)
                <input type="number" min="0" value={draft.price_3_months} onChange={(e) => setDraft({ ...draft, price_3_months: e.target.value, monthly_amount: e.target.value })} />
              </label>
              <label>
                6-month amount (₹)
                <input type="number" min="0" value={draft.price_6_months} onChange={(e) => setDraft({ ...draft, price_6_months: e.target.value })} />
              </label>
              <label>
                12-month amount (₹)
                <input type="number" min="0" value={draft.price_12_months} onChange={(e) => setDraft({ ...draft, price_12_months: e.target.value })} />
              </label>
            </>
          )
        ) : (
          <>
        <label>
          Monthly amount (₹)
          <input type="number" min="0" value={draft.monthly_amount} onChange={(e) => setDraft({ ...draft, monthly_amount: e.target.value })} />
        </label>
        <label>
          Yearly total (₹)
          <input type="number" min="0" value={draft.yearly_amount} onChange={(e) => setDraft({ ...draft, yearly_amount: e.target.value })} placeholder="(monthly − discount) × 12" />
        </label>
        <label>
          Yearly discount per month (₹)
          <input type="number" min="0" value={draft.yearly_discount} onChange={(e) => setDraft({ ...draft, yearly_discount: e.target.value })} />
        </label>
          </>
        )}
        <label>
          Duration (days, monthly cycle)
          <input type="number" min="1" value={draft.duration_days} onChange={(e) => setDraft({ ...draft, duration_days: e.target.value })} />
        </label>
        <label>
          Max flows
          <input type="number" min="0" value={draft.max_flows} onChange={(e) => setDraft({ ...draft, max_flows: e.target.value })} />
        </label>
        <label>
          Max WhatsApp numbers
          <input type="number" min="0" value={draft.max_numbers} onChange={(e) => setDraft({ ...draft, max_numbers: e.target.value })} />
        </label>
        <label>
          Included AI credits
          <input type="number" min="0" value={draft.ai_credits} onChange={(e) => setDraft({ ...draft, ai_credits: e.target.value })} />
        </label>
        <label>
          Credit plan for included credits
          <select value={draft.ai_credit_plan_id} onChange={(e) => setDraft({ ...draft, ai_credit_plan_id: e.target.value })}>
            <option value="">None</option>
            {creditPlans.map((cp) => (
              <option key={cp.id} value={cp.id}>{cp.plan_name} (1 cr = {cp.per_credit_ai_replies} replies)</option>
            ))}
          </select>
        </label>
        <label>
          Features (one per line)
          <textarea rows={7} value={draft.features} onChange={(e) => setDraft({ ...draft, features: e.target.value })} />
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} style={{ width: "auto" }} />
          Active (visible to users)
        </label>
      </div>
      <div className="ping-drawer-foot">
        <button type="button" className="ping-btn ping-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="ping-btn ping-btn-green" disabled={busy} onClick={onSave}>Save plan</button>
      </div>
    </aside>
  </>
);

const QrtPlans = () => {
  const [plans, setPlans] = useState(null);
  const [creditPlans, setCreditPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null); // { id | "new", draft }
  const [busy, setBusy] = useState(false);
  const [creditDrafts, setCreditDrafts] = useState({});
  const [addonDraft, setAddonDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, c, a] = await Promise.all([qrtListPlans(), qrtListCreditPlans(), qrtListAddonPlans()]);
      setPlans(p.data?.data || []);
      setCreditPlans(c.data?.data || []);
      setAddons(a.data?.data || []);
    } catch (err) {
      toast.error("Failed to load plan catalog");
      setPlans([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePlan = async () => {
    const d = editing.draft;
    const isV2 = Number(d.catalog_version) === 2;
    if (!d.plan_display_name || (isV2 ? false : d.monthly_amount === "")) {
      toast.error("Display name and amounts are required");
      return;
    }
    if (isV2 && isTrialPlan(d) && d.price_1_month === "") {
      toast.error("1-month amount is required");
      return;
    }
    if (isV2 && !isTrialPlan(d) && (d.price_3_months === "" || d.price_6_months === "" || d.price_12_months === "")) {
      toast.error("3 / 6 / 12 month amounts are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        plan_display_name: d.plan_display_name,
        monthly_amount: Number(isV2 ? (d.price_3_months || d.price_1_month || d.monthly_amount) : d.monthly_amount),
        yearly_amount: d.yearly_amount === "" ? undefined : Number(d.yearly_amount),
        yearly_discount: Number(d.yearly_discount || 0),
        duration_days: Number(d.duration_days || 30),
        max_flows: Number(d.max_flows || 0),
        max_numbers: Number(d.max_numbers || 0),
        ai_credits: Number(d.ai_credits || 0),
        ai_credit_plan_id: d.ai_credit_plan_id ? Number(d.ai_credit_plan_id) : null,
        features: d.features.split("\n").map((s) => s.trim()).filter(Boolean),
        is_active: d.is_active,
      };
      if (!isV2) payload.plan_name = d.plan_name;
      if (isV2) {
        payload.prices = isTrialPlan(d)
          ? [{ billing_period: "1_month", amount: Number(d.price_1_month), duration_months: 1 }]
          : [
              { billing_period: "3_months", amount: Number(d.price_3_months), duration_months: 3 },
              { billing_period: "6_months", amount: Number(d.price_6_months), duration_months: 6 },
              { billing_period: "12_months", amount: Number(d.price_12_months), duration_months: 12 },
            ];
      }
      if (editing.id === "new") {
        await qrtCreatePlan(payload);
        toast.success("Plan created");
      } else {
        await qrtUpdatePlan(editing.id, payload);
        toast.success("Plan updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save plan");
    } finally {
      setBusy(false);
    }
  };

  const togglePlanActive = async (plan) => {
    try {
      await qrtUpdatePlan(plan.id, { is_active: !(plan.is_active === 1 || plan.is_active === true) });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update plan");
    }
  };

  const saveCreditPlan = async (cp) => {
    const d = creditDrafts[cp.id] || {};
    try {
      await qrtUpdateCreditPlan(cp.id, {
        per_credit_price: d.per_credit_price !== undefined ? Number(d.per_credit_price) : undefined,
        per_credit_ai_replies: d.per_credit_ai_replies !== undefined ? Number(d.per_credit_ai_replies) : undefined,
      });
      toast.success("Credit plan saved");
      setCreditDrafts((m) => ({ ...m, [cp.id]: {} }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save credit plan");
    }
  };

  const saveAddon = async () => {
    const d = addonDraft;
    if (!d.display_name || (d.id === "new" && !d.plan_key)) {
      toast.error("Addon key and display name are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        plan_key: d.plan_key,
        display_name: d.display_name,
        additional_flows: Number(d.additional_flows || 0),
        additional_numbers: Number(d.additional_numbers || 0),
        monthly_amount: Number(d.monthly_amount || 0),
        yearly_amount: Number(d.yearly_amount || 0),
        is_active: d.is_active,
      };
      if (d.id === "new") {
        await qrtCreateAddonPlan(payload);
        toast.success("Addon created");
      } else {
        await qrtUpdateAddonPlan(d.id, payload);
        toast.success("Addon updated");
      }
      setAddonDraft(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save addon");
    } finally {
      setBusy(false);
    }
  };

  if (!plans) return <Spinner />;

  const legacyPlans = plans.filter((p) => catalogVersion(p) !== 2);
  const v2Plans = plans.filter((p) => catalogVersion(p) === 2);

  const renderPlanRows = (rows, v2) => (
    <tbody>
      {rows.map((p) => {
        const active = p.is_active === 1 || p.is_active === true;
        const effYearly = Number(p.monthly_amount) - Number(p.yearly_discount || 0);
        return (
          <tr key={p.id}>
            <td>
              <b>{p.plan_display_name}</b>
              <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{p.plan_name}</div>
            </td>
            {v2 ? (
              isTrialPlan(p) ? (
                <>
                  <td>{fmtMoney(priceOf(p, "1_month") || p.monthly_amount)}</td>
                  <td colSpan={2} style={{ color: "var(--text-3)" }}>one-time</td>
                </>
              ) : (
                <>
                  <td>{fmtMoney(priceOf(p, "3_months"))}</td>
                  <td>{fmtMoney(priceOf(p, "6_months"))}</td>
                  <td>{fmtMoney(priceOf(p, "12_months"))}</td>
                </>
              )
            ) : (
              <>
                <td>{fmtMoney(p.monthly_amount)}</td>
                <td>{fmtMoney(p.yearly_amount)} ({fmtMoney(effYearly)}/mo)</td>
              </>
            )}
            <td>{p.max_flows}</td>
            <td>{p.max_numbers ?? "—"}</td>
            <td>{p.ai_credits || 0}</td>
            <td><span className={`ping-chip ${active ? "g" : "n"}`}>{active ? "Active" : "Hidden"}</span></td>
            <td style={{ whiteSpace: "nowrap" }}>
              <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm"
                onClick={() => setEditing({ id: p.id, draft: planToDraft(p) })}>
                Edit
              </button>{" "}
              <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => togglePlanActive(p)}>
                {active ? "Hide" : "Show"}
              </button>
            </td>
          </tr>
        );
      })}
    </tbody>
  );

  return (
    <div>
      <div className="page-head">
        <div>
          <h2>Plans &amp; add-ons</h2>
          <p>Everything users see on the subscribe page is managed here</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          className="ping-btn ping-btn-green ping-btn-sm"
          onClick={() => setEditing({ id: "new", draft: { ...emptyPlanDraft } })}
        >
          <Plus size={14} /> New plan
        </button>
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head"><h3>Current catalog (legacy)</h3></div>
        <div className="qrt-card-body" style={{ overflowX: "auto" }}>
          <p style={{ fontSize: 12.5, color: "var(--text-3)", margin: "0 0 10px" }}>
            Shown only to users who still have an active monthly/yearly subscription. Do not change these prices to reprice live auto-pay.
          </p>
          <table className="qrt-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Monthly</th>
                <th>Yearly (effective /mo)</th>
                <th>Flows</th>
                <th>Numbers</th>
                <th>AI credits</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            {renderPlanRows(legacyPlans, false)}
          </table>
        </div>
      </div>

      <div className="qrt-card" style={{ marginTop: 12 }}>
        <div className="qrt-card-head"><h3>New catalog (v2)</h3></div>
        <div className="qrt-card-body" style={{ overflowX: "auto" }}>
          <p style={{ fontSize: 12.5, color: "var(--text-3)", margin: "0 0 10px" }}>
            Trial is 1 month one-time. Paid plans bill every 3 / 6 / 12 months. Amounts are exclusive of GST.
          </p>
          <table className="qrt-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>3 months / 1 month</th>
                <th>6 months</th>
                <th>12 months</th>
                <th>Flows</th>
                <th>Numbers</th>
                <th>AI credits</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            {renderPlanRows(v2Plans, true)}
          </table>
        </div>
      </div>

      <div className="qrt-card" style={{ marginTop: 12 }}>
        <div className="qrt-card-head"><h3>AI credit pricing</h3></div>
        <div className="qrt-card-body">
          {creditPlans.map((cp) => (
            <div className="qrt-inline" key={cp.id} style={{ marginBottom: 8 }}>
              <b style={{ fontSize: 13 }}>{cp.plan_name}</b>
              <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>₹ per credit</span>
              <input
                type="number" step="0.01" min="0" style={{ width: 90 }}
                value={creditDrafts[cp.id]?.per_credit_price ?? cp.per_credit_price}
                onChange={(e) => setCreditDrafts((m) => ({ ...m, [cp.id]: { ...m[cp.id], per_credit_price: e.target.value } }))}
              />
              <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>replies per credit</span>
              <input
                type="number" min="1" style={{ width: 80 }}
                value={creditDrafts[cp.id]?.per_credit_ai_replies ?? cp.per_credit_ai_replies}
                onChange={(e) => setCreditDrafts((m) => ({ ...m, [cp.id]: { ...m[cp.id], per_credit_ai_replies: e.target.value } }))}
              />
              {cp.is_adding_plan === 1 && <span className="ping-chip g">Purchase plan</span>}
              <button type="button" className="ping-btn ping-btn-green ping-btn-sm" onClick={() => saveCreditPlan(cp)}>Save</button>
            </div>
          ))}
        </div>
      </div>

      <div className="qrt-card" style={{ marginTop: 12 }}>
        <div className="qrt-card-head">
          <h3>Add-ons</h3>
          <button
            type="button"
            className="ping-btn ping-btn-ghost ping-btn-sm"
            onClick={() => setAddonDraft({ id: "new", plan_key: "", display_name: "", additional_flows: "3", additional_numbers: "0", monthly_amount: "", yearly_amount: "", is_active: true })}
          >
            <Plus size={13} /> New add-on
          </button>
        </div>
        <div className="qrt-card-body" style={{ overflowX: "auto" }}>
          <table className="qrt-table">
            <thead>
              <tr><th>Add-on</th><th>Flows</th><th>Numbers</th><th>Monthly</th><th>Yearly</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {addons.map((a) => {
                const active = a.is_active === 1 || a.is_active === true;
                return (
                  <tr key={a.id}>
                    <td>
                      <b>{a.display_name}</b>
                      <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{a.plan_key}</div>
                    </td>
                    <td>{a.additional_flows}</td>
                    <td>{a.additional_numbers || 0}</td>
                    <td>{fmtMoney(a.monthly_amount)}</td>
                    <td>{fmtMoney(a.yearly_amount)}</td>
                    <td><span className={`ping-chip ${active ? "g" : "n"}`}>{active ? "Active" : "Hidden"}</span></td>
                    <td>
                      <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm"
                        onClick={() => setAddonDraft({
                          id: a.id, plan_key: a.plan_key, display_name: a.display_name,
                          additional_flows: String(a.additional_flows), additional_numbers: String(a.additional_numbers || 0), monthly_amount: String(a.monthly_amount),
                          yearly_amount: String(a.yearly_amount), is_active: active,
                        })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PlanEditor
          title={editing.id === "new" ? "New subscription plan" : `Edit · ${editing.draft.plan_display_name}`}
          draft={editing.draft}
          setDraft={(d) => setEditing({ ...editing, draft: d })}
          creditPlans={creditPlans}
          onSave={savePlan}
          onClose={() => setEditing(null)}
          busy={busy}
          isNew={editing.id === "new"}
        />
      )}

      {addonDraft && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => setAddonDraft(null)} />
          <aside className="ping-drawer" role="dialog" aria-modal="true">
            <div className="ping-drawer-head">
              <div>
                <h3>{addonDraft.id === "new" ? "New flow add-on" : "Edit add-on"}</h3>
                <p>Extra flows on top of the base plan</p>
              </div>
              <button type="button" className="camp-ico" onClick={() => setAddonDraft(null)} aria-label="Close"><X size={16} /></button>
            </div>
            <div className="ping-drawer-body">
              {addonDraft.id === "new" && (
                <label>
                  Key (unique)
                  <input value={addonDraft.plan_key} onChange={(e) => setAddonDraft({ ...addonDraft, plan_key: e.target.value })} placeholder="addon_5flows_monthly" />
                </label>
              )}
              <label>
                Display name
                <input value={addonDraft.display_name} onChange={(e) => setAddonDraft({ ...addonDraft, display_name: e.target.value })} />
              </label>
              <label>
                Additional flows
                <input type="number" min="0" value={addonDraft.additional_flows} onChange={(e) => setAddonDraft({ ...addonDraft, additional_flows: e.target.value })} />
              </label>
              <label>
                Additional WhatsApp numbers
                <input type="number" min="0" value={addonDraft.additional_numbers || "0"} onChange={(e) => setAddonDraft({ ...addonDraft, additional_numbers: e.target.value })} />
              </label>
              <label>
                Monthly amount (₹)
                <input type="number" min="0" value={addonDraft.monthly_amount} onChange={(e) => setAddonDraft({ ...addonDraft, monthly_amount: e.target.value })} />
              </label>
              <label>
                Yearly amount (₹)
                <input type="number" min="0" value={addonDraft.yearly_amount} onChange={(e) => setAddonDraft({ ...addonDraft, yearly_amount: e.target.value })} />
              </label>
              <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={addonDraft.is_active} onChange={(e) => setAddonDraft({ ...addonDraft, is_active: e.target.checked })} style={{ width: "auto" }} />
                Active
              </label>
            </div>
            <div className="ping-drawer-foot">
              <button type="button" className="ping-btn ping-btn-ghost" onClick={() => setAddonDraft(null)}>Cancel</button>
              <button type="button" className="ping-btn ping-btn-green" disabled={busy} onClick={saveAddon}>Save add-on</button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default QrtPlans;
